import { useEffect, type ReactNode } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { getThemeById } from '@/styles/themes/presets';

/**
 * AuthProvider — wraps the app and initializes auth on mount.
 * Must be placed high in the component tree (inside main.tsx or App.tsx).
 *
 * Also applies the fallback theme on first render so CSS variables
 * are always valid — even on routes outside AppShell (e.g. onboarding).
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const initialize = useAuthStore((s) => s.initialize);
  const isHydrated = useAuthStore((s) => s.isHydrated);

  // Apply theme EARLY so CSS variables are valid everywhere (including onboarding).
  // AppShell's useTheme() will refine this once settings are loaded.
  useEffect(() => {
    const saved = window.localStorage.getItem('johnicorn:theme') ?? 'obsidian';
    const theme = getThemeById(saved);
    document.documentElement.setAttribute('data-theme', theme.id);
  }, []);

  useEffect(() => {
    initialize();
  }, [initialize]);

  if (!isHydrated) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-[var(--bg-primary)]">
        <div className="flex flex-col items-center gap-4">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-[var(--xp-gold)] border-t-transparent" />
          <p className="text-sm text-[var(--text-secondary)]">Loading JohnicornOS…</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
